import { useCallback, useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import {
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  Loader2,
  Copy,
  Check,
  Pencil,
  Trash2,
  ExternalLink,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { AdminGate } from "@/components/admin/AdminGate";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { AddReviewForm } from "@/components/admin/AddReviewForm";
import { ClassifyButton } from "@/components/admin/ClassifyButton";
import { CompleteButton } from "@/components/admin/CompleteButton";
import { VerifyTakedowns } from "@/components/admin/VerifyTakedowns";
import { FlagToggle } from "@/components/admin/FlagToggle";
import {
  AdminReviewLifecycleControls,
  type ActionStatus,
  type TakedownAttempt,
} from "@/components/admin/AdminReviewLifecycleControls";
import { CopyReviewUrlButton } from "@/components/customer/CopyReviewUrlButton";
import { resolveAmazonReportText } from "@/lib/amazonSupportMessage";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { api } from "@/lib/api";

type Status = "queued" | "scraping" | "classifying" | "completed" | "failed";
type Violation = { type: string; confidence: number; evidence?: string };

type Review = {
  id: string;
  asin: string;
  amazonReviewId: string;
  reviewUrl: string | null;
  rating: number;
  title: string | null;
  body: string;
  reviewerName: string | null;
  verifiedPurchase: boolean;
  classification: {
    id: string;
    flaggedForTakedown: boolean;
    violations: Violation[];
    rationale: string | null;
    takedownRequest: string | null;
    actionStatus: ActionStatus;
    takedownAttempts: TakedownAttempt[];
  } | null;
};

const VIOLATION_LABELS: Record<string, string> = {
  seller_feedback_misplaced: "Off-topic seller feedback",
  conflict_of_interest: "Conflict of interest",
  incentivized: "Incentivized review",
  profanity_or_harassment: "Profanity / harassment",
  privacy_violation: "Privacy violation",
  off_topic: "Off-topic",
  duplicate_or_template: "Duplicate / template",
  manufacturer_misrepresentation: "Manufacturer misrepresentation",
  competitor_review: "Competitor review",
  review_swap: "Review swap",
};

type Verdict =
  | "pending"
  | "clean"
  | "flagged"
  | "watching"
  | "authorized"
  | "submitted"
  | "removed"
  | "rejected";

function verdictOf(r: Review): Verdict {
  if (!r.classification) return "pending";
  const a = r.classification.actionStatus;
  if (a === "removed") return "removed";
  if (a === "submitted") return "submitted";
  if (a === "authorized") return "authorized";
  if (a === "watching") return "watching";
  if (a === "rejected") return "rejected";
  return r.classification.flaggedForTakedown ? "flagged" : "clean";
}

const PILL_STYLES: Record<Verdict, { cls: string; label: string }> = {
  pending: {
    cls: "bg-muted text-muted-foreground ring-border",
    label: "Pending",
  },
  clean: {
    cls: "bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:ring-emerald-900",
    label: "Clean",
  },
  flagged: {
    cls: "bg-amber-50 text-amber-800 ring-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:ring-amber-900",
    label: "Flagged",
  },
  watching: {
    cls: "bg-muted text-muted-foreground ring-border",
    label: "Watching",
  },
  authorized: {
    cls: "bg-violet-50 text-violet-700 ring-violet-200 dark:bg-violet-950/40 dark:text-violet-300 dark:ring-violet-900",
    label: "Authorized · awaiting filing",
  },
  submitted: {
    cls: "bg-sky-50 text-sky-700 ring-sky-200 dark:bg-sky-950/40 dark:text-sky-300 dark:ring-sky-900",
    label: "Submitted",
  },
  removed: {
    cls: "bg-emerald-100 text-emerald-800 ring-emerald-300 dark:bg-emerald-900/50 dark:text-emerald-200 dark:ring-emerald-800",
    label: "Removed",
  },
  rejected: {
    cls: "bg-red-50 text-red-700 ring-red-200 dark:bg-red-950/40 dark:text-red-300 dark:ring-red-900",
    label: "Rejected",
  },
};

function StatusPill({ verdict }: { verdict: Verdict }) {
  const { cls, label } = PILL_STYLES[verdict];
  return (
    <span
      className={`inline-flex shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${cls}`}
    >
      {label}
    </span>
  );
}

function snippetOf(r: Review, max = 100): string {
  if (r.title) return r.title;
  const body = r.body.replace(/\s+/g, " ").trim();
  return body.length > max ? body.slice(0, max) + "…" : body;
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  const onClick = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // ignore
    }
  };
  return (
    <Button variant="outline" size="sm" onClick={onClick}>
      {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
      {copied ? "Copied" : "Copy"}
    </Button>
  );
}

function ReviewRow({
  review,
  auditId,
  asins,
  onChange,
}: {
  review: Review;
  auditId: string;
  asins: string[];
  onChange: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const verdict = verdictOf(review);
  const violations = review.classification?.violations ?? [];
  const supportDraft = review.classification
    ? resolveAmazonReportText(review)
    : null;
  const amazonReviewUrl =
    review.reviewUrl ??
    (review.amazonReviewId && !review.amazonReviewId.startsWith("manual-")
      ? `https://www.amazon.com/gp/customer-reviews/${review.amazonReviewId}/`
      : null);

  async function onDelete() {
    if (!window.confirm("Delete this review? This also removes its AI verdict.")) {
      return;
    }
    setDeleting(true);
    const res = await api(`/admin/reviews/${review.id}`, { method: "DELETE" });
    setDeleting(false);
    if (!res.ok) {
      alert(res.error ?? "Delete failed");
      return;
    }
    onChange();
  }

  return (
    <li className="overflow-hidden rounded-lg border bg-card ring-1 ring-foreground/5">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-muted/40"
      >
        <StatusPill verdict={verdict} />
        <a
          href={`https://www.amazon.com/dp/${review.asin}`}
          target="_blank"
          rel="noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="font-mono text-xs underline-offset-4 hover:underline"
        >
          {review.asin}
        </a>
        <span className="text-xs text-muted-foreground">{review.rating}★</span>
        {review.verifiedPurchase && (
          <span className="text-[10px] text-emerald-700">verified</span>
        )}
        <span className="min-w-0 flex-1 truncate text-sm text-foreground/80">
          {snippetOf(review)}
        </span>
        {expanded ? (
          <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
        ) : (
          <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
        )}
      </button>

      {expanded && (
        <div className="space-y-3 border-t bg-muted/20 px-4 py-4">
          {editing ? (
            <AddReviewForm
              auditId={auditId}
              asins={asins}
              review={{
                id: review.id,
                asin: review.asin,
                rating: review.rating,
                title: review.title,
                body: review.body,
                reviewerName: review.reviewerName,
                verifiedPurchase: review.verifiedPurchase,
              }}
              onChange={() => {
                setEditing(false);
                onChange();
              }}
              onCancel={() => setEditing(false)}
            />
          ) : (
            <>
          {review.title && (
            <div className="text-sm font-medium">{review.title}</div>
          )}
          {amazonReviewUrl && (
            <div className="flex flex-wrap items-center gap-2">
              <a
                href={amazonReviewUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
              >
                Open Amazon review <ExternalLink className="h-3 w-3" />
              </a>
              <CopyReviewUrlButton url={amazonReviewUrl} />
            </div>
          )}
          <p className="whitespace-pre-wrap text-sm text-foreground/90">
            {review.body}
          </p>

          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setEditing(true)}
            >
              <Pencil className="h-3.5 w-3.5" /> Edit
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={onDelete}
              disabled={deleting}
              className="text-destructive hover:text-destructive"
            >
              <Trash2 className="h-3.5 w-3.5" /> {deleting ? "Deleting…" : "Delete"}
            </Button>
          </div>

          {review.classification ? (
            <>
              <div className="flex flex-wrap items-center gap-2">
                <FlagToggle
                  reviewId={review.id}
                  initialFlagged={review.classification.flaggedForTakedown}
                  onChange={onChange}
                />
                {verdict === "flagged" &&
                  violations.map((v, i) => (
                    <span
                      key={i}
                      className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700 ring-1 ring-inset ring-red-200 dark:bg-red-950/40 dark:text-red-300 dark:ring-red-900"
                    >
                      {VIOLATION_LABELS[v.type] ?? v.type}
                      <span className="tabular-nums opacity-70">
                        {Math.round(v.confidence * 100)}%
                      </span>
                    </span>
                  ))}
                {verdict === "clean" && (
                  <span className="text-xs text-muted-foreground">
                    AI: no policy violations detected
                  </span>
                )}
              </div>
              {review.classification.rationale && (
                <p className="text-xs italic text-muted-foreground">
                  AI: {review.classification.rationale}
                </p>
              )}

              <AdminReviewLifecycleControls
                reviewId={review.id}
                actionStatus={review.classification.actionStatus}
                attempts={review.classification.takedownAttempts}
                onChange={onChange}
              />

              {verdict === "flagged" && supportDraft?.text && (
                <div className="rounded-md border border-border bg-card">
                  <div className="flex items-center justify-between border-b border-border px-3 py-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-xs font-medium text-muted-foreground">
                        Message for Amazon Brand Registry Support
                      </span>
                      <span
                        className={`rounded-full px-2 py-0.5 text-[10px] font-medium ring-1 ring-inset ${
                          supportDraft.source === "stored_draft"
                            ? "bg-sky-50 text-sky-700 ring-sky-200"
                            : "bg-amber-50 text-amber-700 ring-amber-200"
                        }`}
                      >
                        {supportDraft.source === "stored_draft"
                          ? "Saved draft"
                          : "Fallback draft"}
                      </span>
                    </div>
                    <CopyButton text={supportDraft.text} />
                  </div>
                  <pre className="whitespace-pre-wrap px-3 py-3 font-sans text-sm text-foreground">
                    {supportDraft.text}
                  </pre>
                  {supportDraft.source === "deterministic_fallback" && (
                    <p className="border-t border-border px-3 py-2 text-xs text-amber-700">
                      No saved draft was available. Review this standard fallback
                      before submitting it.
                    </p>
                  )}
                </div>
              )}
            </>
          ) : (
            <p className="text-xs text-muted-foreground">
              Not classified yet. Click "Classify new reviews" above.
            </p>
          )}
            </>
          )}
        </div>
      )}
    </li>
  );
}

type AuditDetail = {
  id: string;
  email: string;
  status: Status;
  scrapedReviewCount: number;
  flaggedCount: number;
  inputAsins: string[];
  createdAt: string;
  reviews: Review[];
  reviewCount: number;
  page: number;
  limit: number;
  pageCount: number;
};

const statusVariant: Record<
  Status,
  "default" | "secondary" | "destructive" | "outline"
> = {
  queued: "secondary",
  scraping: "default",
  classifying: "default",
  completed: "outline",
  failed: "destructive",
};

function AdminDetailInner({ auditId }: { auditId: string }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const page = Math.max(1, parseInt(searchParams.get("page") ?? "1", 10) || 1);
  const [audit, setAudit] = useState<AuditDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  const goToPage = useCallback(
    (n: number) => {
      const next = new URLSearchParams(searchParams);
      if (n <= 1) next.delete("page");
      else next.set("page", String(n));
      setSearchParams(next, { replace: false });
    },
    [searchParams, setSearchParams]
  );

  useEffect(() => {
    let cancelled = false;
    api<AuditDetail>(`/admin/audits/${auditId}?page=${page}`).then((res) => {
      if (cancelled) return;
      if (!res.ok) {
        setError(res.error ?? "Audit not found.");
        return;
      }
      setAudit(res.data);
    });
    return () => {
      cancelled = true;
    };
  }, [auditId, reloadKey, page]);

  if (error) return <p className="text-sm text-destructive">{error}</p>;

  if (!audit) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading…
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <Link
          to="/admin/audits"
          className="text-sm text-muted-foreground hover:text-foreground"
        >
          ← All audits
        </Link>
        <a
          href={`/audit/${audit.id}`}
          target="_blank"
          rel="noreferrer"
          className="text-sm text-muted-foreground hover:text-foreground underline-offset-4 hover:underline"
          title="Open the public results page exactly as the lead sees it"
        >
          Preview customer view ↗
        </a>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Audit {audit.id.slice(0, 8)}</CardTitle>
          <CardDescription>{audit.email}</CardDescription>
        </CardHeader>
        <CardContent>
          <dl className="grid grid-cols-2 gap-x-8 gap-y-3 text-sm sm:grid-cols-4">
            <div>
              <dt className="text-muted-foreground">Status</dt>
              <dd className="mt-1">
                <Badge variant={statusVariant[audit.status]}>
                  {audit.status}
                </Badge>
              </dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Reviews entered</dt>
              <dd className="mt-1 tabular-nums">{audit.scrapedReviewCount}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Flagged</dt>
              <dd className="mt-1 tabular-nums font-medium">
                {audit.flaggedCount}
              </dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Created</dt>
              <dd className="mt-1 tabular-nums">
                {audit.createdAt.slice(0, 10)}
              </dd>
            </div>
            <div className="col-span-2 sm:col-span-4">
              <dt className="text-muted-foreground">ASINs</dt>
              <dd className="mt-1 flex flex-wrap gap-x-3 gap-y-1 font-mono">
                {audit.inputAsins.length === 0
                  ? "—"
                  : audit.inputAsins.map((asin) => (
                      <a
                        key={asin}
                        href={`https://www.amazon.com/dp/${asin}`}
                        target="_blank"
                        rel="noreferrer"
                        className="underline-offset-4 hover:underline"
                      >
                        {asin}
                      </a>
                    ))}
              </dd>
            </div>
          </dl>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Add a review</CardTitle>
          <CardDescription>
            Paste fields from the customer&rsquo;s Amazon review page.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <AddReviewForm
            auditId={audit.id}
            asins={audit.inputAsins}
            onChange={reload}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Reviews ({audit.reviews.length})</CardTitle>
          <CardDescription>
            Classify entered reviews against Amazon policy, override the AI
            verdict where needed, then send the lead the results.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <ClassifyButton auditId={audit.id} onChange={reload} />
            <ClassifyButton
              auditId={audit.id}
              onChange={reload}
              force
              label="Re-run AI on all reviews"
              loadingLabel="Re-running…"
            />
            <CompleteButton
              auditId={audit.id}
              alreadyCompleted={audit.status === "completed"}
              leadEmail={audit.email}
              onChange={reload}
            />
          </div>

          <VerifyTakedowns auditId={audit.id} onChange={reload} />

          {audit.reviewCount === 0 ? (
            <p className="text-sm text-muted-foreground">
              No reviews entered yet.
            </p>
          ) : (
            <>
              <ul className="space-y-2">
                {audit.reviews.map((r) => (
                  <ReviewRow
                    key={r.id}
                    review={r}
                    auditId={audit.id}
                    asins={audit.inputAsins}
                    onChange={reload}
                  />
                ))}
              </ul>
              {audit.pageCount > 1 && (
                <div className="flex items-center justify-between pt-2 text-sm text-muted-foreground">
                  <span className="tabular-nums">
                    {(audit.page - 1) * audit.limit + 1}–
                    {Math.min(audit.page * audit.limit, audit.reviewCount)} of{" "}
                    {audit.reviewCount}
                  </span>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={audit.page <= 1}
                      onClick={() => goToPage(audit.page - 1)}
                    >
                      <ChevronLeft className="h-4 w-4" /> Prev
                    </Button>
                    <span className="tabular-nums">
                      Page {audit.page} of {audit.pageCount}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={audit.page >= audit.pageCount}
                      onClick={() => goToPage(audit.page + 1)}
                    >
                      Next <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default function AdminDetail() {
  const { id } = useParams<{ id: string }>();
  if (!id) return null;
  return (
    <AdminGate>
      <AdminLayout>
        <AdminDetailInner auditId={id} />
      </AdminLayout>
    </AdminGate>
  );
}
